import { Component, OnDestroy, OnInit, Inject, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { EnvironmentService } from '@mt-ng2/environment-module';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { NavService } from '@mt-ng2/nav-module';
import { AuthService } from '@mt-ng2/auth-module';

import { finalize } from 'rxjs/operators';
import { markAllFormFieldsAsTouched } from '@mt-ng2/common-functions';
import { PageTitlesService } from '@mt-ng2/page-titles';
import { LoginConfig } from '@mt-ng2/login-module';
import { AppStateService } from '../common/services/app-state.service';

@Component({
    selector: 'app-reset-password',
    template: `
        <ng-progress></ng-progress>
        <div class="login-box">
            <img class="logo-img" src="{{ logoFull }}" alt="Logo" />
            <div class="login-box-body">
                <p class="login-box-msg">
                    {{ config.messageOverrides.resetPasswordLabel }}
                </p>
                <form [formGroup]="resetPasswordForm" (ngSubmit)="onSubmit()">
                    <div class="form-group has-feedback" [class.has-error]="passwordHasError()">
                        <label>New Password</label
                        ><i
                            class="fa fa-info-circle"
                            aria-hidden="true"
                            data-toggle="tooltip"
                            data-html="true"
                            data-placement="right"
                            [title]="config.failedPatternMessage"
                        ></i>
                        <input
                            #Password
                            type="password"
                            autofocus
                            autocomplete="off"
                            class="form-control"
                            placeholder="Password"
                            formControlName="Password"
                        />
                        <span class="fa fa-lock form-control-feedback"></span>
                        <div *ngIf="showPasswordRequiredError()" class="small errortext" [style.position]="'block'">Password is required</div>
                        <div *ngIf="showPasswordMustMatchError()" class="small errortext" [style.position]="'block'">Passwords must match</div>
                        <div *ngIf="hasRegexError()" class="small errortext" [style.position]="'block'" id="failedPatternMessage">
                            {{ config.messageOverrides.failedPattern || config.failedPatternMessage }}
                        </div>
                        <label>Confirm New Password</label>
                        <div class="form-group has-feedback" [class.has-error]="showConfirmPasswordRequiredError()">
                            <input
                                #PasswordConfirm
                                type="password"
                                autocomplete="off"
                                class="form-control"
                                placeholder="Confirm Password"
                                formControlName="ConfirmPassword"
                            />
                            <span class="fa fa-lock form-control-feedback"></span>
                            <div *ngIf="showConfirmPasswordRequiredError()" class="small errortext" [style.position]="'block'">
                                Secondary Password is required
                            </div>
                        </div>
                        <button
                            type="submit"
                            mt-doubleClickDisabled
                            [(doubleClickIsDisabled)]="doubleClickIsDisabled"
                            Class="btn btn-flat btn-success"
                        >
                            Save
                        </button>
                        <a routerLink="/login" class="btn btn-default pull-right"> Go Home </a>
                    </div>
                </form>
            </div>
            <!-- /.login-box-body -->
        </div>
        <!-- /.login-box -->
    `,
})
export class DonorResetPasswordComponent implements OnInit, OnDestroy {
    resetPasswordForm: FormGroup;
    logoFull = `${this.environmentService.config.assetsPath}logo-full.png`;
    doubleClickIsDisabled = false;
    userId: number;
    resetKey: string;

    constructor(
        private fb: FormBuilder,
        private navService: NavService,
        private authService: AuthService,
        private router: Router,
        public route: ActivatedRoute,
        private notificationsService: NotificationsService,
        private environmentService: EnvironmentService,
        public config: LoginConfig,
        private pageTitlesService: PageTitlesService,
        private appStateService: AppStateService,
    ) {}

    ngOnInit(): void {
        // appReady determines if an authenticated connection has been made.
        // While it's waiting it shows a loading icon.  When appReady has a
        // value the loading icon is hidden.  We always want this to be true
        // when you are on the login page.  Because you aren't authed!
        if (this.authService.appReady && !this.authService.appReady.getValue()) {
            this.authService.appReady.next(true);
        }
        this.navService.setShowNav(false);
        this.createForm();
        const queryParams = this.route.snapshot.queryParams;
        this.resetKey = queryParams.resetKey;
        this.userId = +queryParams.userId;
        this.setPageTitle();
    }

    setPageTitle(): void {
        this.pageTitlesService.setPageTitle('Reset Password');
    }

    createForm(): void {
        if (!this.resetPasswordForm) {
            this.resetPasswordForm = this.fb.group({});
        }
        this.resetPasswordForm.addControl('Password', new FormControl('', [Validators.required, Validators.pattern(this.config.passwordPattern)]));
        this.resetPasswordForm.addControl(
            'ConfirmPassword',
            new FormControl('', [Validators.required, Validators.pattern(this.config.passwordPattern)]),
        );
    }

    passwordHasError(): boolean {
        const control = this.resetPasswordForm.get('Password');
        return control.errors && (control.touched || control.errors.maxlength);
    }

    showPasswordRequiredError(): boolean {
        const control = this.resetPasswordForm.get('Password');
        return control.touched && control.hasError('required');
    }

    showConfirmPasswordRequiredError(): boolean {
        const control = this.resetPasswordForm.get('ConfirmPassword');
        return control.touched && control.hasError('required');
    }

    showPasswordMustMatchError(): boolean {
        const control = this.resetPasswordForm.get('Password');
        const controlValue = control.value as string;
        const confirmControl = this.resetPasswordForm.get('ConfirmPassword');
        const confirmControlValue = confirmControl.value as string;
        return control.touched && confirmControl.touched && controlValue.length && confirmControlValue.length && controlValue !== confirmControlValue;
    }

    hasRegexError(): boolean {
        let answer = false;
        let passwordControl = this.resetPasswordForm.controls.Password;
        if (passwordControl.errors && passwordControl.errors.pattern) {
            answer = true;
            passwordControl.setErrors({ pattern: true });
        }
        return answer;
    }

    onSubmit(): void {
        const form = this.resetPasswordForm;
        if (this.authService.matchPassword(form, null)) {
            if (form.valid) {
                this.authService
                    .resetPassword(this.userId, form.value.Password, form.value.ConfirmPassword, this.resetKey)
                    .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                    .subscribe(
                        (answer) => {
                            this.success();
                            this.appStateService.resolveAppState();
                        },
                        (error) => {
                            if (error.status === 418) {
                                this.notificationsService.error(LoginConfig.signInFailure);
                            } else if (error.status === 400) {
                                // TODO: Can we just inject the error service?
                                if (error.error) {
                                    this.error(error.error.ModelState.Service);
                                } else {
                                    this.error('Something went wrong, Please generate a new reset email');
                                    console.error(`Oops something went wrong!
                                - Probably the key has been used or expired.
                                - Or you did something you weren't supposed to do.
                                - Your best bet is to just generate a new email.`);
                                }
                            }
                        },
                    );
            } else {
                markAllFormFieldsAsTouched(form);
                this.enableDoubleClick();
            }
        } else {
            this.error('Passwords do not match');
            this.enableDoubleClick();
        }
    }

    enableDoubleClick(): void {
        setTimeout(() => {
            this.doubleClickIsDisabled = false;
        });
    }

    error(msg?: string): void {
        if (!msg) {
            this.notificationsService.error('Save Failed');
        } else {
            this.notificationsService.error(msg);
        }
    }

    success(): void {
        this.notificationsService.success('Password Updated Successfully');
    }

    ngOnDestroy(): void {
        this.navService.setShowNav(true);
    }
}
